import { useCallback } from 'react';

import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { EntityTypeahead } from '@/lib/crm/components/EntityTypeahead';
import type { EntityResult, EntityType } from '@/lib/search';

export interface MergeRecordPickerDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number;
  searchTypes: EntityType[];
  currentEntityId: string;
  onPick: (entityId: string) => void;
}

// EntityTypeahead requires onChange, but this picker only cares about the
// terminal onSelect — selecting an entity closes the dialog immediately, so
// there is no controlled value to track.
function noopEntityTypeaheadChange(): void {}

export function MergeRecordPickerDialog({
  isOpen,
  onOpenChange,
  orgId,
  searchTypes,
  currentEntityId,
  onPick,
}: MergeRecordPickerDialogProps) {
  const handleSelect = useCallback(
    (entity: EntityResult) => {
      if (entity.entity_id === currentEntityId) {
        return;
      }
      onPick(entity.entity_id);
      onOpenChange(false);
    },
    [currentEntityId, onPick, onOpenChange]
  );

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal className='max-w-lg'>
        <Dialog title='Merge records' instance='merge-records'>
          <div className='space-y-3 pb-2'>
            <p className='text-sm text-fg-secondary'>
              Search for the record to merge into this one.
            </p>
            <EntityTypeahead
              orgId={orgId}
              types={searchTypes}
              onChange={noopEntityTypeaheadChange}
              onSelect={handleSelect}
              placeholder='Search records'
            />
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
